import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Github, Mail, Shield, Zap, Globe, Users, Coffee } from 'lucide-react';

const LoginPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated, login, loading } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const isDev = import.meta.env.DEV;

  // Redirect if already authenticated
  if (!loading && isAuthenticated) {
    return <Navigate to="/translate" replace />;
  }

  const handleLogin = (provider) => {
    setIsLoggingIn(true);
    login(provider);
    
    // Reset loading state after delay (for mock auth)
    if (isDev) {
      setTimeout(() => setIsLoggingIn(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary rounded-full flex items-center justify-center mb-6">
            <Globe className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            {t('login.title')}
          </h2>
          <p className="mt-2 text-gray-600">
            {t('login.subtitle')}
          </p>
          
          {/* Development Mode Notice */}
          {isDev && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-700">
                <Coffee className="h-4 w-4" />
                <span className="text-sm font-medium">{t('login.developmentMode')}</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {t('login.mockAuth')}
              </p>
            </div>
          )}
        </div>

        {/* Authentication Notice - Only in production */}
        {!isDev && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="bg-green-100 rounded-full p-2 flex-shrink-0">
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-green-900 mb-1">{t('login.authNoticeTitle')}</h3>
                <p className="text-xs text-green-700 leading-relaxed">
                  {t('login.authNoticeDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('login.whySignIn')}</h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm text-gray-700">{t('login.features.fast')}</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-sm text-gray-700">{t('login.features.secure')}</span>
            </div>
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-sm text-gray-700">{t('login.features.improve')}</span>
            </div>
          </div>
        </div>

        {/* Login Options */}
        <div className="space-y-4">
          <button
            onClick={() => handleLogin('google')}
            disabled={isLoggingIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="h-5 w-5" />
            {isLoggingIn ? t('login.signingIn') : `${t('login.continueGoogle')}${isDev ? ` ${t('login.mock')}` : ''}`}
          </button>

          <button
            onClick={() => handleLogin('github')}
            disabled={isLoggingIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Github className="h-5 w-5" />
            {isLoggingIn ? t('login.signingIn') : `${t('login.continueGithub')}${isDev ? ` ${t('login.mock')}` : ''}`}
          </button>
        </div>

        {/* Development Tools */}
        {isDev && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">{t('login.developmentTools')}</h4>
            <div className="space-y-2 text-xs text-gray-600">
              <p>{t('login.devInfo.line1')}</p>
              <p>{t('login.devInfo.line2')}</p>
              <p>{t('login.devInfo.line3')}</p>
              <p>{t('login.devInfo.line4')}</p>
            </div>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            {t('login.privacyNote')}
          </p>
        </div>

        {/* Beta Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 rounded-full p-1">
              <Shield className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-900">{t('login.betaAccess')}</h4>
              <p className="text-xs text-blue-700 mt-1">
                {t('login.betaDescription')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;